import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthBaseUrl } from 'utils/authApi';
import { URLS } from 'utils/tests/urls.config';

// Authentication state for the introducer portal. The portal serves both
// Cognito-authenticated iwoca staff (impersonating an introducer) and
// Django-authenticated introducer customers, so we have to detect either.
//
// Pattern adapted from credit-compass-frontend's useAuth hook.
//
// Tradeoff (sequential vs parallel probes): the /profile fallback is gated
// on `cognito.isError`, so the two requests run sequentially rather than in
// parallel. That saves a /profile call on the cognito-success path (which
// becomes the common case post-migration) at the cost of an extra
// round-trip of latency for django-only users mid-migration: their cognito
// probe must 401 before /profile starts. We accept the temporary latency
// cost during migration; once customers are fully migrated to Cognito the
// fallback becomes a no-op and the hook collapses to a single /auth/me
// call. If the latency hit becomes painful in practice, drop the
// `cognito.isError` guard to fire both in parallel.

type TCognitoMeResponse = {
  sub: string;
  email: string;
  [key: string]: unknown;
};

type TLendingProfileResponse = {
  data: {
    user_id?: number;
    user_type?: 'staff' | 'customer' | 'broker' | string;
    impersonator_type?: string;
    [key: string]: unknown;
  };
};

const fetchCognitoMe = async (): Promise<TCognitoMeResponse> => {
  // eslint-disable-next-line iwoca/prefer-lapi-client
  const response = await fetch(`${getAuthBaseUrl()}/me`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`auth /me ${response.status}`);
  }
  return response.json();
};

const fetchLendingProfile = async (): Promise<TLendingProfileResponse> => {
  // Skip lendingApiFetch — its hardcoded service token authenticates
  // anonymous callers as the public-frontend user, so /profile would
  // return 200 and useAuth would resolve to logged-in for everyone.
  // eslint-disable-next-line iwoca/prefer-lapi-client
  const response = await fetch(URLS.profile(), {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`lending /profile ${response.status}`);
  }
  return response.json();
};

type TAuthState = {
  isLoggedIn: boolean;
  isLoading: boolean;
  authType: 'cognito' | 'django' | 'none';
};

/**
 * Resolves the current user's logged-in state.
 *
 * Calls the Cognito-backed /auth/me endpoint on auth-service first; only
 * falls back to the LAPI /profile call (legacy Django session) once
 * Cognito has resolved with an error. The user is logged in if either
 * succeeds.
 *
 * Replaces the legacy `getCookie('isLoggedIn') === 'True'` pattern, which
 * relied on iwoca-django's IsLoggedInMiddleware stamping a cookie on every
 * response — that breaks for endpoints that bypass auth at the gateway.
 */
export const useAuth = (): TAuthState => {
  const cognito = useQuery({
    queryKey: ['auth', 'cognitoMe'],
    queryFn: fetchCognitoMe,
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const userProfile = useQuery({
    queryKey: ['auth', 'lendingProfile'],
    queryFn: fetchLendingProfile,
    enabled: cognito.isError,
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  if (cognito.isSuccess) {
    return { isLoggedIn: true, isLoading: false, authType: 'cognito' };
  }

  if (userProfile.isSuccess) {
    return { isLoggedIn: true, isLoading: false, authType: 'django' };
  }

  // Loading covers both the initial cognito probe and the django fallback
  // it triggers — only resolve to logged-out once both have failed.
  if (cognito.isPending || (cognito.isError && !userProfile.isError)) {
    return { isLoggedIn: false, isLoading: true, authType: 'none' };
  }

  return { isLoggedIn: false, isLoading: false, authType: 'none' };
};

// Call after flows that change session state (e.g. registration POSTs)
// so useAuth refetches instead of serving the cached anonymous result.
export const useInvalidateAuth = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['auth'] });
};
